import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography } from "@mui/material";

import useSession from "hooks/api/auth/useSession";
import { useGetTeams } from "hooks/api/useGetTeams";
import { updateTeamUser } from "api/team";
import useSaveIntakeProgress from "hooks/api/useSaveIntakeProgress";
import ANLogoSrc from "assets/an_purple_gray.png";
import { ANLoadingScreen } from "@answersnow/pkg-theme";
import NewTeamMemberForm from "./NewTeamMemberForm";
import { ClientCookie } from "../utils";

const cookieClientId = new ClientCookie();

function UpdateMemberInfo({ email, userId, clientId, isIntakeFlow, initialData }) {
  const { t } = useTranslation(["auth"]);
  let { session, sessionLoading } = useSession();

  // Avoid calling the getTeams if there is no session
  const teamQuery = session ? { userId } : null;
  let { teams, teamsLoading } = useGetTeams(teamQuery);

  // Guard on the data, not just isSuccess: the query can succeed with an EMPTY list
  // (a brand-new invitee has no team association yet), in which case teams[0] is
  // undefined and reading .name crashes the whole setup flow.
  const teamName = teams?.[0]?.name || t("auth:updateMemberInfo.defaultTeamName");

  const { mutateAsync: saveProgress } = useSaveIntakeProgress(clientId);

  // Save the client ID to a cookie
  if (!cookieClientId.get() && session) {
    cookieClientId.set({
      clientId,
      userId,
    });
    cookieClientId.save();
  }

  const handleSubmit = async (values) => {
    const updatedValues = {
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: values.phoneNumber,
      ...values.address,
    };

    try {
      await updateTeamUser({ userId, data: updatedValues });
    } catch (err) {
      console.error("Failed to update team user:", err);
      throw err;
    }

    if (isIntakeFlow) {
      try {
        await saveProgress({
          stage: "profileInformation",
          subStage: "profileInformation",
          isStageCompleted: true,
          isSubStageCompleted: true,
          isSpecialist: false,
          subStageData: updatedValues,
        });
      } catch (err) {
        console.error("Failed to save intake progress:", err);
      }
    }

    location.href = "/";
  };

  if (teamsLoading || sessionLoading) {
    return <ANLoadingScreen />;
  }

  return (
    <Grid container justifyContent="center">
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        sx={{ width: "320px", maxWidth: "320px", height: "100vh", marginTop: "60px" }}
      >
        <Grid item xs={12}>
          <Box component="header" textAlign="center">
            <img src={ANLogoSrc} alt="logo purple and gray" width={198} height={48} />

            <Typography variant="h2" component="h2" sx={{ marginTop: 7 }}>
              {t("auth:updateMemberInfo.title")}
            </Typography>

            <Typography variant="body1" sx={{ marginTop: 3 }}>
              {t("auth:updateMemberInfo.subtitle", { teamName })}
            </Typography>
          </Box>

          <Box component="section" sx={{ marginTop: 7 }}>
            <NewTeamMemberForm email={email} onSubmit={handleSubmit} initialData={initialData} />
          </Box>
        </Grid>

        <Grid item xs={12} component="footer" textAlign="center" sx={{ marginTop: 7 }}>
          <Typography component="p" variant="body2">
            {t("auth:updateMemberInfo.questionsPrompt")}
          </Typography>
          <Typography component="span" variant="body2" color="primary">
            804-215-5600
          </Typography>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default UpdateMemberInfo;
